import { Injectable } from '@angular/core';
import { LoadingController } from '@ionic/angular';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import * as moment from 'moment';
import { environment } from 'src/environments/environment';
import { RecordType } from '../const/column-page';
import * as constants from '../const/constants';
import { Md5 } from 'ts-md5/dist/md5';
import { GetURL } from '../const/requests-methods';

@Injectable({
    providedIn: 'root'
})

export class CommonService {

    public loading: any;
    public tagCondition = 'and';

    constructor(private loadingCtrl: LoadingController) { }

    decodeAuthToken(token: string) {
        if (!token) {
            return null;
        }

        try {
            return jwtDecode<JwtPayload>(token);
        } catch (exception) {
            console.log('Failed to decode auth token!');
        }

        return null;
    }

    safeBase64Decode(value: string) {
        if (value) {
            try {
                return atob(value);
            } catch (exception) {
                console.log('String not in bas64 format!');
            }
        }

        return null;
    }

    safeJsonDecode(value: string) {
        if (value) {
            try {
                return JSON.parse(value);
            } catch (exception) {
                console.log('String not in JSON format!');
            }
        }

        return null;
    }

    async startLoader() {
        this.loading = await this.loadingCtrl.create({
            message: 'Loading...',
            spinner: 'circles'
        });
        this.loading.present();
    }

    async stopLoading() {
        if (this.loading) {
            await this.loading.dismiss();
        }
    }

    waitForLoadingToSync() {
        if (this.loading) {
            this.stopLoading();
        } else {
            setTimeout(() => {
                this.waitForLoadingToSync();
            });
        }
    }

    safeDateFormat(value: any, format: string = constants.CONFIGURATION.DEFAULT_DATE_FORMAT) {
        if (value) {
            try {
                return moment(value).format(format);
            } catch (exception) {
                console.log('Date is in inccorect format!');
            }
        }

        return null;
    }

    safeMonthFormat(value, format: string = constants.CONFIGURATION.DEFAULT_MONTH_FORMAT) {
        if (value) {
            try {
                return moment(value, 'MM').format(format);
            } catch (exception) {
                console.log('Date is in inccorect format!');
            }
        }

        return null;
    }

    getYearMonth(value) {
        if (value) {
            try {
                return moment(value).format('YYYYMM');
            } catch (exception) {
                console.log('Date is in inccorect format!');
            }
        }

        return null;
    }

    getMonth(value) {
        if (value) {
            try {
                return moment(value).format('MM');
            } catch (exception) {
                console.log('Date is in inccorect format!');
            }
        }

        return null;
    }

    getYear(value) {
        if (value) {
            try {
                return moment(value).format('YYYY');
            } catch (exception) {
                console.log('Date is in inccorect format!');
            }
        }

        return null;
    }

    isValidEmail(email: string): boolean {
        try {
            return constants.CONFIGURATION.EMAIL_VALIDATION_PATTERN.test(String(email)?.trim()?.toLowerCase());
        } catch (exception) {
            return false;
        }
    }

    generateUserInitials(name: string) {
        try {
            if (name) {
                const initials = [];
                const nameParts = name?.split(' ');

                if (nameParts.length > 0) {
                    initials.push(nameParts[0].charAt(0));
                }

                if (nameParts.length > 1) {
                    initials.push(nameParts[1].charAt(0));
                }

                return initials?.join('');
            }
        } catch (error) {
            console.log('Failed to generate user initials!');
        }

        return null;
    }

    getIconForRecordType(recordType: string) {
        const typeToIconMap = {
            [RecordType.PDF]: 'far fa-file-pdf',
            [RecordType.WORD]: 'far fa-file-word',
            [RecordType.EXCEL]: 'far fa-file-excel',
            [RecordType.POWERPOINT]: 'far fa-file-powerpoint',
            [RecordType.AUDIO]: 'far fa-file-audio',
            [RecordType.VIDEO]: 'far fa-file-video',
            [RecordType.IMAGE]: 'far fa-file-image',
            [RecordType.ZIP]: 'far fa-file-archive'
        };

        return typeToIconMap[recordType] || 'far fa-file-alt';
    }


    getProfilePictureUrl(userId: number) {
        if (userId) {
            return environment.apiUrl + GetURL.GET_PROFILE_PICTURE + '/' + Md5.hashStr(userId?.toString()) + '.jpg?t=' + moment().unix();
        }

        return null;
    }

    getUserFullName(user?: any) {
        const fullName = [user?.firstName, user?.lastName]?.join(' ');
        return fullName?.trim() ? fullName : user?.name;
    }
}
